import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./InviteCodeModal.css";

const InviteCodeModal = ({
  isOpen,
  onClose,
  onProceed,
  error,
  selectedRole,
  isLoading,
  defaultCode,
  message,
}) => {
  const [inviteCode, setInviteCode] = useState("");
  const [isCodeValid, setIsCodeValid] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [autoVerified, setAutoVerified] = useState(false);
  const { hostId, code } = useParams();
  const navigate = useNavigate();

  const isUsher = window.location.pathname.includes("/join-Usher");
  const role = isUsher ? "usher" : "guest";

  useEffect(() => {
    if (error) {
      setIsSubmitting(false);
      setIsCodeValid(false);
      setErrorMessage(error);
    }
  }, [error]);

  useEffect(() => {
    if (defaultCode) {
      setInviteCode(defaultCode);
    }
  }, [defaultCode]);

  useEffect(() => {
    if (isOpen) {
      if (!defaultCode) {
        setInviteCode("");
      }
      setIsCodeValid(true);
      setIsSubmitting(false);
      setErrorMessage("");
    }
  }, [isOpen, defaultCode]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setErrorMessage("");
    setIsCodeValid(true);

    if (!inviteCode || inviteCode.length !== 6) {
      setIsCodeValid(false);
      setErrorMessage("Invalid invite code. Please enter a 6-digit code.");
      return;
    }

    setIsSubmitting(true);

    try {
      await onProceed(inviteCode);
    } catch (err) {
      const msg =
        err?.message || "Invalid invitation code. Please check and try again.";
      setIsCodeValid(false);
      setErrorMessage(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setErrorMessage("");
    setIsCodeValid(true);
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="card-wrapper">
        <div className="invite-modal-content">
          <div className="top-bar">
            <button className="close-button-primary" onClick={handleClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 6L6 18"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M6 6L18 18"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          <div className="modal-header">
            <h2 className="code-modal-title">Join Event</h2>
            <p className="code-modal-subtitle">
              {hostId && code
                ? `You're joining as ${
                    role === "usher" ? "an Usher" : "a Guest"
                  }!`
                : "Provide the invite code below"}
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="invite-code" className="code-input-label">
              Code
            </label>

            <input
              id="invite-code"
              type="text"
              className={`input-field ${
                !isCodeValid || errorMessage ? "invalid-input" : ""
              }`}
              value={inviteCode}
              onChange={(e) => {
                const cleaned = e.target.value
                  .replace(/[^0-9]/g, "")
                  .slice(0, 6);
                setInviteCode(cleaned);
                if (!isCodeValid) setIsCodeValid(true);
                if (errorMessage) setErrorMessage("");
              }}
              placeholder="Enter 6-digit code"
              maxLength={6}
              disabled={!!(hostId && code)}
            />

            {errorMessage && (
              <p className="invalid-code" role="alert">
                {errorMessage}
              </p>
            )}
          </div>

          <div className="button-container">
            <button className="closed-button" onClick={handleClose}>
              Close
            </button>

            <button
              className="proceedCode-button"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Verifying..." : "Proceed"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteCodeModal;